import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'const';
import KeySignupPopup from './KeySignupPopup';
import ShopSettingsService from 'services/ShopSettingsService';
import { STORAGE_KEYS } from 'const';
import Loader from 'components/utils/Loading';
import { AlertMessagesContext } from 'react-alert-messages';
import SubscriptionService from 'services/SubscriptionService';
import { ShopContext } from 'Context/ShopContext';
import TerminalService from 'services/TerminalServices';

function SubscriptionEndPopup() {
  const history = useHistory();
  const { shop, setShop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');

  const [showCouponPopup, setShowCouponPopup] = useState(false);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [terminals, setTerminals] = useState([]);

  useEffect(() => {
    const fetchTerminals = async () => {
      try {
        const data = await TerminalService.getTerminals();
        if (data && Array.isArray(data)) {
          setTerminals(data);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchTerminals();
  }, []);

  const totalTerminals = terminals.length;
  const posCount = terminals.filter((t) => t.type === 'pos').length;
  const posWebCount = terminals.filter((t) => t.type === 'pos-web').length;
  const posWindowsCount = terminals.filter((t) => t.type === 'pos-windows').length;
  const ecsCount = terminals.filter((t) => t.type === 'ecs').length;

  let additionalPosCount = posCount;
  let additionalPosWebCount = posWebCount;
  let additionalPosWindowsCount = posWindowsCount;
  let additionalEcsCount = ecsCount;

  if (totalTerminals > 0) {
    if (posCount > 0) {
      additionalPosCount -= 1;
    } else if (posWebCount > 0) {
      additionalPosWebCount -= 1;
    } else if (posWindowsCount > 0) {
      additionalPosWindowsCount -= 1;
    } else if (ecsCount > 0) {
      additionalEcsCount -= 1;
    }
  }

  const hasAdditionalTerminals =
    additionalPosCount > 0 || additionalPosWebCount > 0 || additionalPosWindowsCount > 0 || additionalEcsCount > 0;

  const clearAndReloadShopProfile = async () => {
    const resp = await ShopSettingsService.clearAndReloadShopProfile();
    setShop(resp);
  };

  const handleSubscriptionRenewalWithKey = async () => {
    setLoadingIndicator(true);
    try {
      const secret = localStorage.getItem(STORAGE_KEYS.REGISTRSATION_KEY);
      await SubscriptionService.renewSubscriptionWithKey({ secret });
      await clearAndReloadShopProfile();
      setShowCouponPopup(false);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  return (
    <>
      <Loader isOpen={loadingIndicator} />
      <div className={styles.popupOverlay}>
        <div className={styles.popupBackdrop}></div>
        <div className={styles.popupModal}>
          <div className={styles.popupHeader}>
            <div className={styles.popupHeaderContent}>
              <div className={`${styles.popupIcon} ${styles.popupIconExpired}`}>
                <WarningRoundedIcon size={24} />
              </div>
              <div>
                <h2 className={styles.popupTitle}>{tPopup('Subscription Expired')}</h2>
                <p className={styles.popupPlan}>
                  {shop?.subscription?.name} {tCommon('Plan')}
                </p>
              </div>
            </div>
          </div>

          <div className={styles.popupContent}>
            <div className={styles.popupMessage}>
              <div>
                <p>
                  {tPopup(
                    'Your subscription has expired. To continue enjoying features and uninterrupted access, please renew your plan now.'
                  )}
                </p>

                <div className={styles.popupTerminalBox}>
                  <div className={styles.popupTerminalPlanName}>{shop?.subscription?.name || tPopup('Plan')}</div>
                  {totalTerminals > 1 && hasAdditionalTerminals && (
                    <>
                      <hr className={styles.popupTerminalDivider} />
                      <div className={styles.popupTerminalTitle}>{tPopup('Additional Terminals')}</div>
                      <div className={styles.popupTerminalCounts}>
                        {[
                          additionalEcsCount > 0 ? `ECS : ${additionalEcsCount}` : null,
                          additionalPosCount > 0 ? `POS : ${additionalPosCount}` : null,
                          additionalPosWebCount > 0 ? `POS Web : ${additionalPosWebCount}` : null,
                          additionalPosWindowsCount > 0 ? `POS Win : ${additionalPosWindowsCount}` : null,
                        ]
                          .filter(Boolean)
                          .map((text, idx, arr) => (
                            <React.Fragment key={idx}>
                              <span>{text}</span>
                              {idx < arr.length - 1 && <span style={{ margin: '0 8px', color: '#ccc' }}>|</span>}
                            </React.Fragment>
                          ))}
                      </div>
                    </>
                  )}
                </div>

                <div className={`${styles.popupAlert} ${styles.popupAlertExpired}`}>
                  {tPopup('⚠️ Access to features has been suspended')}
                </div>
              </div>
            </div>

            <div className={styles.popupActions}>
              {!shop.dealer || shop?.dealer?.is_online_renewal_enabled ? (
                <button
                  className={`${styles.popupRenew} ${styles.popupRenewExpired}`}
                  onClick={() => history.push(ROUTES.RENEW_PLAN)}
                >
                  {tPopup('Renew Now')}
                </button>
              ) : (
                <button
                  className={`${styles.popupRenew} ${styles.popupRenewExpired}`}
                  onClick={() => setShowCouponPopup(true)}
                >
                  {tPopup('Renew with Coupon')}
                </button>
              )}
              {(!shop.dealer || shop?.dealer?.is_online_renewal_enabled) && (
                <div className={styles.coupon}>
                  <span className={styles.popupCoupon} onClick={() => setShowCouponPopup(true)}>
                    {tPopup('Renew with Coupon?')}
                  </span>
                </div>
              )}
            </div>

            <div className={styles.popupFooter}>
              <p>
                {tPopup('Questions about your subscription?')}{' '}
                <button
                  className={styles.supportLink}
                  onClick={() => {
                    const message = `${shop?.shop_name} (${shop?.id})\nneed to renew subscription`;
                    window.open(
                      `https://wa.me/${shop?.dealer?.customer_support_number}?text=${encodeURIComponent(message)}`,
                      '_blank'
                    );
                  }}
                >
                  {tCommon('Contact Support')}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
      {showCouponPopup && (
        <KeySignupPopup
          handleClose={() => setShowCouponPopup(false)}
          handleKeyRegister={handleSubscriptionRenewalWithKey}
        />
      )}
    </>
  );
}

export default SubscriptionEndPopup;
